/**
 * Importar a rede: desenho (DWG/DXF) e dados reais (EPANET + KML) — Nexora Grid (frontend, US-406 e US-2301).
 *
 * Criado em: 16/10/2026 · Última modificação: 02/11/2026 · Versão: 1.2.0
 *
 * v1.2 (US-4603, Sprint 46): cabeçalho com migalhas e o indicador de passos (arquivo → conferência → importação →
 * resultado); a escolha do que importar virou um seletor segmentado.
 *
 * GUS-1103: o fuso nunca é deduzido; a engenharia escolhe o EPSG, confere o centro da rede no mapa e só então
 * confirma. O arquivo é processado e apagado pelo backend.
 * US-2301 (dados reais): o `.inp` EPANET de uma rede da operadora (ex.: Costa Rica) vem com o KML de
 * conferência opcional e um nome de rede; a prévia mostra nós/trechos/bombas/setores e o centro no mapa,
 * e nada é gravado antes da confirmação.
 */
import { useEffect, useState } from "react";
import { api, ErroApi } from "../api";
import { CabecalhoTela, Segmentado } from "../componentes/Tela";
import { useSessao } from "../sessao";
import { MapaPosicao } from "./MapaPosicao";

const FUSOS: [number, string][] = Array.from({ length: 21 }, (_, i) => 31965 + i).map((e) => {
  const zona = e <= 31976 ? `${e - 31965 + 11}N` : `${e - 31977 + 17}S`;
  return [e, `SIRGAS 2000 / UTM ${zona} (EPSG ${e})`];
});

type PreviaDxf = {
  codigo_confirmacao: string; centro: { lat: number; lon: number }; mapa_conferencia: string; avisos: string[];
  contagens: Record<string, unknown>; ativos: { novos: number; atualizados: number; iguais: number; protegidos: number;
    protegidos_com_diferenca: { identificacao: string; diferencas: string[] }[] }; ja_importado: boolean;
};

type PreviaEpanet = {
  codigo_confirmacao: string; centro: { lat: number; lon: number }; mapa_conferencia: string; avisos: string[];
  contagens: Record<string, number> & { nos_por_tipo?: Record<string, number> };
  bombas: Record<string, string>; setores: string[];
  kml: { pontos: Record<string, number>; divisas: number } | null; ja_importado: boolean;
};

type Modo = "dxf" | "dados-reais";

export function Importar() {
  const { empresa } = useSessao();
  const [municipios, setMunicipios] = useState<{ codigo: string; nome: string; cadastrado: boolean }[]>([]);
  const [importacoesReais, setImportacoesReais] = useState<{ id: string; rede: string; arquivo: string; municipio: string | null; vigente: boolean }[]>([]);
  const [municipio, setMunicipio] = useState("");
  const [municipiosPorImportacao, setMunicipiosPorImportacao] = useState<Record<string, string>>({});
  const [modo, setModo] = useState<Modo>("dxf");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [arquivoInp, setArquivoInp] = useState<File | null>(null);
  const [arquivoKml, setArquivoKml] = useState<File | null>(null);
  const [rede, setRede] = useState("");
  const [epsg, setEpsg] = useState(31981);
  const [epsgEpanet, setEpsgEpanet] = useState(31982);
  const [previa, setPrevia] = useState<PreviaDxf | null>(null);
  const [previaEpanet, setPreviaEpanet] = useState<PreviaEpanet | null>(null);
  const [confirmado, setConfirmado] = useState(false);
  const [estado, setEstado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const recarregarMunicipios = async () => {
    if (!empresa) return;
    const [m, i] = await Promise.all([
      api<{ municipios: { codigo: string; nome: string; cadastrado: boolean }[] }>(`/v1/municipios?empresa=${empresa}`),
      api<typeof importacoesReais>(`/v1/ativos/dados-reais?empresa=${empresa}`),
    ]);
    setMunicipios(m.municipios.filter((item) => item.cadastrado));
    setImportacoesReais(i);
  };
  useEffect(() => { void recarregarMunicipios().catch(() => undefined); }, [empresa]); // eslint-disable-line react-hooks/exhaustive-deps
  const formularioEpanet = (extra: Record<string, string> = {}) => {
    const f = new FormData();
    f.set("empresa", empresa ?? "");
    f.set("municipio", municipio);
    f.set("epsg", String(epsgEpanet));
    f.set("rede", rede.trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "") || "dados-reais");
    Object.entries(extra).forEach(([k, v]) => f.set(k, v));
    f.set("inp", arquivoInp!);
    if (arquivoKml) f.set("kml", arquivoKml);
    return f;
  };
  const verPreviaEpanet = async () => {
    setErro(null); setPreviaEpanet(null); setConfirmado(false); setEstado("lendo o modelo…");
    try { setPreviaEpanet(await api<PreviaEpanet>("/v1/ativos/dados-reais/previa", { form: formularioEpanet() })); setEstado(null); }
    catch (e) { setErro(e instanceof ErroApi ? e.message : String(e)); setEstado(null); }
  };
  const importarEpanet = async () => {
    setErro(null); setEstado("importando os dados reais…");
    try {
      const r = await api<{ id: string; rede: string; ja_importado: boolean }>("/v1/ativos/dados-reais",
        { form: formularioEpanet({ codigo_confirmacao: previaEpanet!.codigo_confirmacao }) });
      await recarregarMunicipios();
      setEstado(r.ja_importado ? "Este arquivo com este fuso (e o KML) já tinha sido importado: nada mudou."
        : `Importação concluída: a rede “${r.rede}” agora usa os dados reais (nós e trechos geolocalizados).`);
    } catch (e) { setErro(e instanceof ErroApi ? e.message : String(e)); setEstado(null); }
  };
  const associarMunicipio = async (id: string) => {
    setErro(null); setEstado("associando importação ao município…");
    try {
      await api(`/v1/ativos/dados-reais/${id}/municipio`, { metodo: "PUT", corpo: { municipio: municipiosPorImportacao[id] } });
      await recarregarMunicipios();
      setEstado("Associação municipal salva.");
    } catch (e) { setErro(e instanceof ErroApi ? e.message : String(e)); setEstado(null); }
  };
  const verPreviaDxf = async () => {
    setErro(null); setPrevia(null); setConfirmado(false); setEstado("lendo o desenho…");
    try { setPrevia(await api<PreviaDxf>("/v1/ativos/importacoes/previa", { form: formularioDxf() })); setEstado(null); }
    catch (e) { setErro(e instanceof ErroApi ? e.message : String(e)); setEstado(null); }
  };
  const importarDxf = async () => {
    setErro(null); setEstado("importando…");
    try {
      const r = await api<{ resultado: { novos: number; atualizados: number }; ja_importado: boolean }>("/v1/ativos/importacoes",
        { form: formularioDxf({ codigo_confirmacao: previa!.codigo_confirmacao }) });
      setEstado(r.ja_importado ? "Este arquivo com este fuso já tinha sido importado: nada mudou."
        : `Importação concluída: ${r.resultado.novos} ativos novos e ${r.resultado.atualizados} atualizados, todos pendentes de validação.`);
    } catch (e) { setErro(e instanceof ErroApi ? e.message : String(e)); setEstado(null); }
  };
  const formularioDxf = (extra: Record<string, string> = {}) => {
    const f = new FormData();
    f.set("empresa", empresa ?? "");
    f.set("epsg", String(epsg));
    Object.entries(extra).forEach(([k, v]) => f.set(k, v));
    f.set("arquivo", arquivo!);
    return f;
  };
  const trocarModo = (novo: Modo) => {
    setModo(novo); setPrevia(null); setPreviaEpanet(null); setConfirmado(false); setErro(null); setEstado(null);
  };
  const c = previa?.contagens as Record<string, number> | undefined;
  // US-4603: passo do assistente (1 arquivo, 2 conferência da prévia, 3 confirmação, 4 resultado)
  const temPrevia = modo === "dxf" ? !!previa : !!previaEpanet;
  const passo = estado && /importad|gravad|conclu/i.test(estado) ? 4 : temPrevia ? (confirmado ? 3 : 2) : 1;
  const ce = previaEpanet?.contagens;
  return (
    <section className="detalhe-ativo tl-pagina">
      <CabecalhoTela voltar="/cadastro" migalhas={[{ texto: "Cadastro", para: "/cadastro" }, { texto: "Importar rede" }]} titulo="Importar rede"
        subtitulo="Nada é gravado antes da sua confirmação" />
      <ol className="tl-passos" aria-label="Passos da importação">
        {["Arquivo", "Conferência", "Importação", "Resultado"].map((p, i) => (
          <li key={p} aria-current={passo === i + 1 ? "step" : undefined} className={passo > i + 1 ? "feito" : undefined}>{p}</li>
        ))}
      </ol>
      <div className="tl-linha-acoes">
        <span className="nx-suave" id="rotulo-modo">O que vai ser importado</span>
        <Segmentado rotulo="O que vai ser importado" valor={modo} aoMudar={(v) => trocarModo(v as Modo)}
          opcoes={[{ valor: "dxf", texto: "Desenho do abastecimento (DWG/DXF)" }, { valor: "dados-reais", texto: "Dados reais da rede (EPANET + KML)" }]} />
      </div>
      {modo === "dxf" && (
        <>
          <div className="nx-cartao grade-ficha">
            <div className="nx-campo campo-largo" data-obrigatorio>
              <label htmlFor="arquivo">Arquivo DXF (converta o DWG antes com o LibreDWG)</label>
              <input id="arquivo" type="file" accept=".dxf" onChange={(e) => { setArquivo(e.target.files?.[0] ?? null); setPrevia(null); }} />
            </div>
            <div className="nx-campo campo-largo" data-obrigatorio>
              <label htmlFor="epsg">Fuso do desenho (confirme com o cliente)</label>
              <select id="epsg" value={epsg} onChange={(e) => { setEpsg(Number(e.target.value)); setPrevia(null); }}>
                {FUSOS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </div>
            <button type="button" className="nx-botao nx-botao-principal" disabled={!arquivo || !empresa || !!estado}
              onClick={() => void verPreviaDxf()}>Ver prévia</button>
          </div>
          {estado && <p className="nx-suave" role="status">{estado}</p>}
          {erro && <p className="erro" role="alert">{erro}</p>}
          {previa && c && (
            <div className="duas-colunas">
              <div className="nx-cartao">
                <h3>Prévia — nada foi gravado</h3>
                <ul className="lista-previa">
                  <li>{c.trechos_rede} trechos de distribuição, {c.nos} nós, {c.casas_ligadas} casas ligadas, {c.setores} setores</li>
                  <li>Ativos: <b>{previa.ativos.novos}</b> novos, {previa.ativos.atualizados} atualizados, {previa.ativos.iguais} iguais,
                    {" "}{previa.ativos.protegidos} protegidos (validados ou da engenharia: não serão alterados)</li>
                  {previa.ativos.protegidos_com_diferenca.slice(0, 10).map((p) => (
                    <li key={p.identificacao} className="nx-suave">{p.identificacao}: desenho difere em {p.diferencas.join(", ")}</li>
                  ))}
                  {previa.avisos.map((a) => <li key={a} className="aviso">⚠ {a}</li>)}
                  {previa.ja_importado && <li className="aviso">Este arquivo com este fuso já foi importado.</li>}
                </ul>
                <label className="confirmar">
                  <input type="checkbox" checked={confirmado} onChange={(e) => setConfirmado(e.target.checked)} />
                  Conferi no mapa: o centro da rede está na cidade certa
                </label>
                <button type="button" className="nx-botao nx-botao-principal" disabled={!confirmado || !!estado}
                  onClick={() => void importarDxf()}>Importar</button>
              </div>
              <div className="nx-cartao">
                <h3>Centro da rede com o fuso escolhido</h3>
                <MapaPosicao latitude={previa.centro.lat} longitude={previa.centro.lon} editavel={false} aoMudar={() => undefined} />
                <a href={previa.mapa_conferencia} target="_blank" rel="noreferrer">abrir no OpenStreetMap ›</a>
              </div>
            </div>
          )}
        </>
      )}
      {modo === "dados-reais" && (
        <>
          <div className="nx-cartao grade-ficha">
            <div className="nx-campo campo-largo" data-obrigatorio>
              <label htmlFor="municipio-epanet">Município desta rede</label>
              <select id="municipio-epanet" required value={municipio} onChange={(e) => { setMunicipio(e.target.value); setPreviaEpanet(null); setConfirmado(false); }}>
                <option value="">Selecione o município cadastrado</option>{municipios.map((m) => <option key={m.codigo} value={m.codigo}>{m.nome} ({m.codigo})</option>)}
              </select>
              {!municipios.length && <small className="aviso">Cadastre o município em Empresas antes de importar a rede.</small>}
            </div>
            <div className="nx-campo campo-largo" data-obrigatorio>
              <label htmlFor="inp">Modelo hidráulico EPANET (.inp; o sistema importa vazões em l/s)</label>
              <input id="inp" type="file" accept=".inp" onChange={(e) => { setArquivoInp(e.target.files?.[0] ?? null); setPreviaEpanet(null); }} />
            </div>
            <div className="nx-campo campo-largo">
              <label htmlFor="kml">KML de conferência (opcional: RAP/ETA do QGIS do cliente)</label>
              <input id="kml" type="file" accept=".kml" onChange={(e) => { setArquivoKml(e.target.files?.[0] ?? null); setPreviaEpanet(null); }} />
            </div>
            <div className="nx-campo" data-obrigatorio>
              <label htmlFor="rede">Nome da rede (ex.: costarica — não substitui a rede do desenho)</label>
              <input id="rede" type="text" value={rede} placeholder="costarica"
                onChange={(e) => { setRede(e.target.value); setPreviaEpanet(null); }} />
            </div>
            <div className="nx-campo campo-largo" data-obrigatorio>
              <label htmlFor="epsg-epanet">Fuso das coordenadas do modelo (confirme com o cliente)</label>
              <select id="epsg-epanet" value={epsgEpanet} onChange={(e) => { setEpsgEpanet(Number(e.target.value)); setPreviaEpanet(null); }}>
                {FUSOS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </div>
            <button type="button" className="nx-botao nx-botao-principal"
              disabled={!arquivoInp || !rede.trim() || !municipio || !empresa || !!estado}
              onClick={() => void verPreviaEpanet()}>Ver prévia</button>
          </div>
          {estado && <p className="nx-suave" role="status">{estado}</p>}
          {erro && <p className="erro" role="alert">{erro}</p>}
          {importacoesReais.some((i) => !i.municipio) && <div className="nx-cartao">
            <h3>Importações antigas sem município</h3>
            <p className="nx-suave">Associe cada importação ao município confirmado na prévia original. Essa ação não altera os arquivos nem as coordenadas.</p>
            {importacoesReais.filter((i) => !i.municipio).map((i) => <div className="linha-botoes" key={i.id}>
              <span>{i.rede} · {i.arquivo}{i.vigente ? " · vigente" : ""}</span>
              <select aria-label={`Município da importação ${i.rede}`} value={municipiosPorImportacao[i.id] ?? ""}
                onChange={(e) => setMunicipiosPorImportacao({ ...municipiosPorImportacao, [i.id]: e.target.value })}>
                <option value="">Selecione o município</option>{municipios.map((m) => <option key={m.codigo} value={m.codigo}>{m.nome} ({m.codigo})</option>)}
              </select>
              <button type="button" className="nx-botao" disabled={!municipiosPorImportacao[i.id] || !!estado}
                onClick={() => void associarMunicipio(i.id)}>Associar</button>
            </div>)}
          </div>}
          {previaEpanet && ce && (
            <div className="duas-colunas">
              <div className="nx-cartao">
                <h3>Prévia — nada foi gravado</h3>
                <ul className="lista-previa">
                  <li>{ce.nos} nós ({ce.nos_por_tipo?.juncao ?? 0} junções, {ce.nos_por_tipo?.fonte ?? 0} fontes,
                    {" "}{ce.nos_por_tipo?.reservatorio ?? 0} reservatórios), {ce.trechos} trechos, {ce.valvulas} válvulas</li>
                  <li>Bombas com curva: {Object.keys(previaEpanet.bombas).length} <span className="nx-suave">
                    ({Object.entries(previaEpanet.bombas).slice(0, 4).map(([id, nome]) => `${id} ${nome}`).join("; ")})</span></li>
                  <li>Setores pela TAG: {previaEpanet.setores.join(", ") || "nenhum (trechos ficam em SETOR-OUTROS)"}</li>
                  {previaEpanet.kml &&
                    <li className="nx-suave">KML de conferência: {Object.values(previaEpanet.kml.pontos).reduce((a, b) => a + b, 0)} pontos
                      {" "}({Object.entries(previaEpanet.kml.pontos).map(([t, q]) => `${q} ${t}`).join(", ")}), {previaEpanet.kml.divisas} divisas</li>}
                  {previaEpanet.avisos.map((a) => <li key={a} className="aviso">⚠ {a}</li>)}
                  {previaEpanet.ja_importado && <li className="aviso">Este arquivo com este fuso e KML já foi importado.</li>}
                </ul>
                <label className="confirmar">
                  <input type="checkbox" checked={confirmado} onChange={(e) => setConfirmado(e.target.checked)} />
                  Confirmei o município selecionado e conferi o centro da rede no mapa
                </label>
                <button type="button" className="nx-botao nx-botao-principal" disabled={!confirmado || !!estado}
                  onClick={() => void importarEpanet()}>Importar dados reais</button>
              </div>
              <div className="nx-cartao">
                <h3>Centro da rede com o fuso escolhido</h3>
                <MapaPosicao latitude={previaEpanet.centro.lat} longitude={previaEpanet.centro.lon} editavel={false} aoMudar={() => undefined} />
                <a href={previaEpanet.mapa_conferencia} target="_blank" rel="noreferrer">abrir no OpenStreetMap ›</a>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
